import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface HUDSelectProps<T extends string> {
  label: string;
  index: string;
  testId: string;
  options: readonly T[];
  value: T | null;
  onChange: (v: T | null) => void;
  placeholder?: string;
  optional?: boolean;
}

export function HUDSelect<T extends string>({
  label, index, testId, options, value, onChange, placeholder = "Select…", optional,
}: HUDSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "group relative w-full rounded-2xl border bg-white/[0.03] text-left backdrop-blur-xl transition-[border-color,box-shadow] duration-300",
          open ? "border-safety/70 shadow-[0_0_32px_-12px_rgba(255,91,0,0.55)]" : "border-cad/30 hover:border-cad/60",
        )}
      >
        <div className="flex items-center justify-between px-4 pt-3">
          <span className={cn("font-mono text-[10px] uppercase tracking-[0.22em] transition-colors duration-300", open ? "text-safety" : "text-cad")}>
            {label}
            {optional && <span className="ml-2 text-cad/60">· optional</span>}
          </span>
          <span className="font-mono text-[10px] tracking-[0.18em] text-cad/60">{index}</span>
        </div>
        <div className="flex items-center justify-between px-4 pb-3.5 pt-1.5">
          <span data-testid={`${testId}-value`} className={cn("font-sans text-base", value ? "text-white" : "text-white/25")}>
            {value ?? placeholder}
          </span>
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="text-cad group-hover:text-white">
            <ChevronDown size={16} />
          </motion.span>
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            data-testid={`${testId}-listbox`}
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-x-0 top-[calc(100%+8px)] z-30 max-h-64 overflow-y-auto rounded-2xl border border-cad/40 bg-obsidian-deep/95 p-1.5 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur-2xl"
          >
            {optional && (
              <Option testId={`${testId}-option-none`} label="No province" muted selected={value === null} onSelect={() => { onChange(null); setOpen(false); }} />
            )}
            {options.map((opt) => (
              <Option key={opt} testId={`${testId}-option-${opt.toLowerCase().replace(/\s+/g, "-")}`} label={opt} selected={value === opt} onSelect={() => { onChange(opt); setOpen(false); }} />
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function Option({ label, selected, muted, onSelect, testId }: { label: string; selected: boolean; muted?: boolean; onSelect: () => void; testId: string }) {
  return (
    <li role="option" aria-selected={selected}>
      <button
        type="button"
        data-testid={testId}
        onClick={onSelect}
        className={cn(
          "flex w-full items-center justify-between rounded-xl px-3.5 py-2.5 text-left font-sans text-sm transition-colors duration-150",
          selected ? "bg-safety/10 text-safety" : muted ? "text-white/40 hover:bg-white/5 hover:text-white/70" : "text-white/80 hover:bg-white/5 hover:text-white",
        )}
      >
        {label}
        {selected && <Check size={14} />}
      </button>
    </li>
  );
}
